import moment from 'moment';
import React, { useCallback, useState } from 'react';
import { FaPrint } from 'react-icons/fa6';
import { MdOutlineModeEditOutline } from 'react-icons/md';
import { Link, useParams } from 'react-router-dom';
import { ActionIcon, Button, Text, Tooltip } from 'rizzui';
import { toast } from 'sonner';
import StatusDropdown from '../../../../../components/forms/StatusDropdown';
import { usePrivilege } from '../../../../../components/priviledge/PriviledgeProvider';
import { useFiltersPR } from '../../../../../components/providers/FiltersProvider';
import CustomTable from '../../../../../components/table/CustomTable';
import { FilterDrawerView } from '../../../../../components/table/components/TableFilter';
import useInvoiceStates from '../../../../../hooks/api-calls/invoices/useInvoiceStates';
import useKoReasons from '../../../../../hooks/api-calls/useKoReasons';
import useHandleDownloadDocument from '../../../../../hooks/useDownloadDocument';
import useFetch from '../../../../../hooks/useFetch';
import useHandleErrors from '../../../../../hooks/useHandleErrors';
import Page from '../../../../../layout/Page/Page';
import { menuRoutes } from '../../../../../router/menu';
import { ClientService } from '../../../../../services/client/clientService';
import { InvoiceService } from '../../../../../services/invoice/invoiceService';
import { InvoicesApiResponse } from '../../../../../type/entities/invoice-type';
import { fixNumber } from '../../../../../utils/formatNumber';
import ClientInvoicesFilters from './ClientInvoicesFilters';

interface ClientInvoicesProps { }

const columnOrder = [
    'id',
    'number',
    'totalPrice',
    'taxableAmount',
    'tax',
    'totalTaxesAmount',
    'totalProducts',
    'currentStatus',
    'corporation',
    'invoiceDate',
    'paymentDate',
    'expirationDate',
    'createdAt',
    'updatedAt',
    'invoiceDates',
];

const ClientInvoices: React.FC<ClientInvoicesProps> = ({ }) => {

    const { id = '' } = useParams<{ id: string }>();
    const { userCan } = usePrivilege();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();
    const { handleDownloadDocumentNewTab } = useHandleDownloadDocument();
    const service = new ClientService();

    const { getInvoiceStatesList } = useInvoiceStates({ active: true });
    const { koReasonsOptions } = useKoReasons();
    const { handleErrors } = useHandleErrors();
    const [openFilters, setOpenFilters] = useState<boolean>(false);

    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        const response = await service.listInvoices({ ...filters, filter_filters: { ...filters.filter_filters, client: id } });
        return response.getResponseData() as InvoicesApiResponse;
    }, [filters]));

    const handleStateChange = async (invoiceId: string, newStateId: string, comment: string, koReasonId?: string) => {
        try {
            const response = await (new InvoiceService()).changeInvoiceState(invoiceId, newStateId, comment, koReasonId);
            const responseData = response.getResponseData();

            if (responseData && responseData.success) {
                toast.success("Estado de la factura actualizado correctamente");
                refetch();
            } else if (responseData) {
                toast.error(responseData.message);
                refetch();
            }
        } catch (error: any) {
            handleErrors(error);
        }
    };

    const handleDownloadMultiInvoices = async (selectedRows: any) => {
        try {
            const response = await (await (new InvoiceService()).printMultiInvoices(selectedRows)).getResponseData();
            if (response) {
                // Crear un enlace temporal para descargar el archivo
                const url = window.URL.createObjectURL(new Blob([response], { type: 'application/zip' }));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `facturas.zip`);
                document.body.appendChild(link);
                link.click();
                link.parentNode?.removeChild(link);
                window.URL.revokeObjectURL(url);
            } else {
                toast.error('No se han podido descargar las facturas seleccionadas.');
            }
        } catch (error) {
            handleErrors(error);
        }
    };

    /**
     * Order the data to match the column order
     */
    const orderedData = data?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });

    return (
        <Page container='fluid' className='mt-6'>
            {data && (
                <CustomTable
                    id={'invoices-table'}
                    largeTable
                    data={orderedData}
                    columnOrder={columnOrder}
                    isLoading={loading}
                    columnsNotShown={['company', 'lines', 'invoiceDate', 'paymentDate', 'expirationDate', 'invoiceDates']}
                    additionalColumns={[
                        {
                            id: 'invoiceDates',
                            header: 'Fechas Factura',
                            render: (row: any) => {
                                const invoiceDate = row.row.original?.invoiceDate.date;
                                const paymentDate = row.row.original?.paymentDate?.date;
                                const expirationDate = row.row.original?.expirationDate?.date;
                                return (
                                    <ul className='gap-x-4'>
                                        {invoiceDate && (
                                            <li className='flex flex-nowrap pb-1'>
                                                <h6 className='text-nowrap text-gray-400'>Fecha de Factura:&nbsp;</h6>
                                                {invoiceDate ? moment(invoiceDate).format('DD/MM/YY') : 'N/A'}
                                            </li>
                                        )}
                                        {paymentDate && (
                                            <li className='flex flex-nowrap pb-1'>
                                                <h6 className='text-nowrap text-gray-400'>Fecha de Pago:&nbsp;</h6>
                                                {paymentDate ? moment(paymentDate).format('DD/MM/YY') : 'N/A'}
                                            </li>
                                        )}
                                        {expirationDate && (
                                            <li className='flex flex-nowrap pb-1'>
                                                <h6 className='text-nowrap text-gray-400'>Fecha de Vencimiento:&nbsp;</h6>
                                                {expirationDate ? moment(expirationDate).format('DD/MM/YY') : 'N/A'}
                                            </li>
                                        )}
                                    </ul>
                                );
                            }
                        },
                    ]}
                    overrideColumns={[
                        {
                            key: 'number',
                            columnNameKey: 'invoiceNumber',
                            render: (row: any) => {
                                if (userCan('get_invoices', 'invoices')) {
                                    return (
                                        <Link to={`${menuRoutes.invoices.path}/${row.row.original.id}/profile${menuRoutes.invoices.profile.info}`}>
                                            <Text className='font-bold text-primary'>{row.row.original?.number}</Text>
                                        </Link>
                                    );
                                } else {
                                    return row.row.original?.number || 'N/A';
                                }
                            },
                        },
                        {
                            key: 'totalPrice',
                            render: (row: any) => {
                                return (
                                    <div className="text-center">
                                        {row.row.original?.totalPrice ? `${fixNumber(row.row.original?.totalPrice)}€` : 'N/A'}
                                    </div>
                                )
                            },
                        },
                        {
                            key: 'taxableAmount',
                            render: (row: any) => {
                                return (
                                    <div className="text-center">
                                        {row.row.original?.taxableAmount ? `${fixNumber(row.row.original?.taxableAmount)}€` : 'N/A'}
                                    </div>
                                )
                            },
                        },
                        {
                            key: 'currentStatus',
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={row.row.original?.currentStatus?.description}
                                        placement="top"
                                        color="invert"
                                    >
                                        <StatusDropdown
                                            entityId={row.row.original?.id}
                                            currentState={row.row.original?.currentStatus}
                                            statesOptions={getInvoiceStatesList(row.row.original?.currentStatus?.id)}
                                            handleStateChange={handleStateChange}
                                            title="Esta acción modificará el estado de la factura"
                                            koReasonsOptions={koReasonsOptions}
                                            koReasonStatusEntity="invoice"
                                        />
                                    </Tooltip>
                                );
                            }
                        },
                        {
                            key: 'corporation',
                            render: (row: any) => {
                                return row.row.original?.corporation?.name || 'N/A';
                            },
                        },
                        {
                            key: 'tax',
                            render: (row: any) => {
                                return (
                                    row.row.original?.tax ? `${row.row.original?.tax}%` : 'N/A'
                                );
                            },
                        },
                        {
                            key: 'totalTaxesAmount',
                            render: (row: any) => {
                                return (
                                    <div className="text-center">
                                        {row.row.original?.totalTaxesAmount ? `${fixNumber(row.row.original?.totalTaxesAmount)}€` : 'N/A'}
                                    </div>
                                )

                            },
                        },
                        {
                            key: 'totalProducts',
                            render: (row: any) => {
                                return (
                                    <div className="text-center">
                                        {row.row.original?.totalProducts ? `${row.row.original?.totalProducts}` : 'N/A'}
                                    </div>
                                )
                            },
                        },
                    ]}
                    actions={[
                        {
                            label: 'Generar PDF',
                            permissions: { group: 'invoices', permission: 'print_invoices' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Generar PDF'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 cursor-pointer"
                                            onClick={() => handleDownloadDocumentNewTab(service.generateInvoicePdf, row.id, moment().format('DD-MM-YYYY') + '_factura.pdf')}
                                        >
                                            <FaPrint className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Edit',
                            permissions: { group: 'invoices', permission: 'edit_invoices' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Editar factura'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Link to={`${menuRoutes.invoices.path}/${row.id}/edit`}>
                                            <ActionIcon
                                                as="span"
                                                size="sm"
                                                variant="outline"
                                                className="hover:!border-gray-900 hover:text-gray-700"
                                            >
                                                <MdOutlineModeEditOutline className="h-4 w-4" size={20} />
                                            </ActionIcon>
                                        </Link>
                                    </Tooltip>
                                );
                            },
                        }
                    ]}
                    multiActions={[
                        {
                            label: 'Download Invoices',
                            render: (selectedRows: any) => {
                                return (
                                    <Button
                                        onClick={() => { handleDownloadMultiInvoices(selectedRows) }}
                                        variant="outline"
                                        color="secondary"
                                        size="sm"
                                    >
                                        Descargar Facturas
                                    </Button>
                                );
                            }
                        },
                    ]}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: (data as InvoicesApiResponse) ? data.lastPage : 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    toggleFilters={() => setOpenFilters(!openFilters)}
                />
            )}

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros Facturas (Cliente)'}>
                <ClientInvoicesFilters filters={filters} updateFilters={updateFilters} resetFilters={resetFilters} />
            </FilterDrawerView>
        </Page>
    );
};

export default ClientInvoices;